import { useTranslation } from "react-i18next";
import { DeletableBadge, Input, Label } from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import type { AllergyResponse } from "@tellescope/types-models";

const ALLERGY_SEVERITIES = ["mild", "moderate", "severe"] as const;

export type AllergySeverity = (typeof ALLERGY_SEVERITIES)[number];

export interface SelectedAllergyEditorProps {
  allergy: AllergyResponse;
  onChange: (allergy: AllergyResponse) => void;
  onRemove: () => void;
}

export const SelectedAllergyEditor = ({ allergy, onChange, onRemove }: SelectedAllergyEditorProps) => {
  const { t } = useTranslation("forms");

  const severityOptions = [
    { value: "mild" as const, label: t("Mild") },
    { value: "moderate" as const, label: t("Moderate") },
    { value: "severe" as const, label: t("Severe") },
  ];

  const severity = ALLERGY_SEVERITIES.find((option) => option === allergy.severity);

  return (
    <div className="flex flex-col gap-2 rounded-md border p-2">
      <div className="flex items-start justify-between gap-2">
        <DeletableBadge onDelete={onRemove}>{allergy.display}</DeletableBadge>
        <span className="text-xs text-muted-foreground shrink-0">{allergy.code}</span>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`allergy-${allergy.code}-severity`}>{t("Severity")}</Label>
        <Select
          value={severity}
          onValueChange={(newSeverity: AllergySeverity) => onChange({ ...allergy, severity: newSeverity })}
        >
          <Select.Trigger
            id={`allergy-${allergy.code}-severity`}
            aria-label={t("Severity for {{allergy}}", { allergy: allergy.display })}
          >
            <Select.Value placeholder={t("Select severity")} />
          </Select.Trigger>
          <Select.Content>
            {severityOptions.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`allergy-${allergy.code}-note`}>{t("Note")}</Label>
        <Input
          id={`allergy-${allergy.code}-note`}
          value={allergy.note ?? ""}
          onChange={(e) => onChange({ ...allergy, note: e.target.value })}
          placeholder={t("Add a note")}
          aria-label={t("Note for {{allergy}}", { allergy: allergy.display })}
        />
      </div>
    </div>
  );
};
